// Cleans up Android Share intent payloads before using them as word lookup queries.
// Android often appends the source URL and surrounding sentence fragments to the shared text.

export const STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'is', 'it', 'he', 'she', 'we', 'i', 'you', 'they',
  'was', 'be', 'been', 'are', 'by', 'from', 'this', 'that', 'not',
  'as', 'if', 'so', 'do', 'did', 'has', 'have', 'had', 'will', 'would',
  'can', 'could', 'should', 'may', 'might', 'must', 'shall',
])

export interface SanitizedShare {
  /** First non-stopword token — ready to use as the search query. */
  query: string
  /** Full text after decoding and URL removal — show as context or edit fallback. */
  rawDecoded: string
}

/**
 * Strip URLs and extract the first meaningful word from a share-intent payload.
 *
 * Steps:
 *   1. Percent-decode (handles double-encoded sequences; ignores errors).
 *   2. Remove all http/https URLs.
 *   3. Find the first non-stopword token as `query`.
 *   4. If nothing meaningful remains, fall back to the full decoded text so the
 *      user can see what was shared and edit it manually.
 */
export function sanitizeShareText(raw: string): SanitizedShare {
  let decoded: string
  try {
    decoded = decodeURIComponent(raw)
  } catch {
    decoded = raw
  }

  const withoutUrls = decoded.replace(/https?:\/\/\S+/g, '').replace(/\s+/g, ' ').trim()
  // If the entire payload was a URL, preserve the decoded string as context
  const rawDecoded = withoutUrls || decoded.trim()

  // Strip leading/trailing punctuation from each token (handles trailing commas, quotes, etc.)
  const tokens = rawDecoded
    .split(/\s+/)
    .map(t => t.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, ''))
    .filter(Boolean)

  const query =
    tokens.find(t => !STOPWORDS.has(t.toLowerCase())) ??
    tokens[0] ??
    rawDecoded

  return { query, rawDecoded }
}
